import {
  selectFiltersQuery,
  selectIsCompareMode,
  selectLifecycleFiltersQuery,
} from 'util/selector/querySelector';
import {
  selectReportOrCurrentProjectId,
  selectReportProjectId,
} from 'util/selector/reportSelector';
import { selectHasMatch } from 'util/selector/routerSelector';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { SummaryPulseComparedModel } from 'api/dto/new/SummaryPulseCompared.dto';
import { ReportRoute } from 'app/route/report';
import { projectScoreApi } from 'container/projectScoreApi';
import { summaryApi } from 'container/summaryApi';
import { SimpleProject } from 'model/SimpleProject';
import { Summary } from 'model/Summary';
import {
  selectCurrentProjectId,
  selectIsPulseOrTemplateProjectKind,
} from 'reduxStore/project/selectors';
import { STORE_NAME } from 'reduxStore/summary/initialState';
import { abortAndRefreshSignalByKey } from 'shared/utils/api/abortAndRefreshSignalByKey';
import { serializeError } from 'shared/utils/redux';
import { SummaryPulseCombinedModel } from 'api/dto/new/SummaryPulseCombined.dto';
import { setEngagementDriverByCategory } from 'reduxStore/engagementDrivers/asyncActions';

export const fetchSummaryPulse = createAsyncThunk<any>(
  `${STORE_NAME}/fetchSummaryPulse`,
  async (_, { getState, dispatch }) => {
    const isPulseOrTemplateProjectKind = selectIsPulseOrTemplateProjectKind(getState());
    const isCompareMode = selectIsCompareMode(getState());

    if (!isPulseOrTemplateProjectKind) {
      return;
    }

    if (isCompareMode) {
      await dispatch(fetchSummaryPulseCompared());
    } else {
      await dispatch(fetchSummaryPulseCombined());
      dispatch(setEngagementDriverByCategory());
    }
  },
  { serializeError }
);

export const fetchSummaryPulseCombined = createAsyncThunk<SummaryPulseCombinedModel | void>(
  `${STORE_NAME}/fetchSummaryPulseCombined`,
  (_, { getState }) => {
    const signal = abortAndRefreshSignalByKey(fetchSummaryPulseCombined.typePrefix);
    const projectId = selectReportOrCurrentProjectId(getState());
    const query = selectFiltersQuery(getState());

    return summaryApi.summaryPulseCombined({ projectId, query }, { signal });
  },
  { serializeError }
);

export const fetchSummaryPulseCompared = createAsyncThunk<SummaryPulseComparedModel | void>(
  `${STORE_NAME}/fetchSummaryPulseCompared`,
  (_, { getState }) => {
    const signal = abortAndRefreshSignalByKey(fetchSummaryPulseCompared.typePrefix);
    const projectId = selectReportOrCurrentProjectId(getState());
    const query = selectFiltersQuery(getState());

    return summaryApi.summaryPulseCompared({ projectId, query }, { signal });
  },
  { serializeError }
);

export const fetchSummaryPulseOnDemand = createAsyncThunk<void>(
  `${STORE_NAME}/fetchSummaryPulseOnDemand`,
  (_, { getState, dispatch }) => {
    const isCompareMode = selectIsCompareMode(getState());

    isCompareMode
      ? dispatch(fetchSummaryPulseOnDemandCompared())
      : dispatch(fetchSummaryPulseOnDemandCombined());
  },
  { serializeError }
);

export const fetchSummaryPulseOnDemandCombined = createAsyncThunk<SimpleProject.Combine | void>(
  `${STORE_NAME}/fetchSummaryPulseOnDemandCombined`,
  (_, { getState }) => {
    const signal = abortAndRefreshSignalByKey(fetchSummaryPulseOnDemandCombined.typePrefix);
    const projectId = selectReportOrCurrentProjectId(getState());
    const query = selectFiltersQuery(getState());

    return summaryApi.summaryPulseOnDemandCombined({ projectId, query }, { signal });
  },
  { serializeError }
);

export const fetchSummaryPulseOnDemandCompared = createAsyncThunk<SimpleProject.Compare | void>(
  `${STORE_NAME}/fetchSummaryPulseOnDemandCompared`,
  (_, { getState }) => {
    const signal = abortAndRefreshSignalByKey(fetchSummaryPulseOnDemandCompared.typePrefix);
    const projectId = selectReportOrCurrentProjectId(getState());
    const query = selectFiltersQuery(getState());

    return summaryApi.summaryPulseOnDemandCompared({ projectId, query }, { signal });
  },
  { serializeError }
);

export const fetchSummaryPulseLifecycle = createAsyncThunk<void>(
  `${STORE_NAME}/fetchSummaryPulseLifecycle`,
  (_, { getState, dispatch }) => {
    const isCompareMode = selectIsCompareMode(getState());

    isCompareMode
      ? dispatch(fetchSummaryPulseLifecycleCompared())
      : dispatch(fetchSummaryPulseLifecycleCombined());
  },
  { serializeError }
);

export const fetchSummaryPulseLifecycleCombined = createAsyncThunk<SimpleProject.Combine | void>(
  `${STORE_NAME}/fetchSummaryPulseLifecycleCombined`,
  (_, { getState }) => {
    const signal = abortAndRefreshSignalByKey(fetchSummaryPulseLifecycleCombined.typePrefix);
    const projectId = selectReportOrCurrentProjectId(getState());
    const query = selectLifecycleFiltersQuery(getState());

    return summaryApi.summaryPulseLifecycleCombined({ projectId, query }, { signal });
  },
  { serializeError }
);

export const fetchSummaryPulseLifecycleCompared = createAsyncThunk<SimpleProject.Compare | void>(
  `${STORE_NAME}/fetchSummaryPulseLifecycleCompared`,
  (_, { getState }) => {
    const signal = abortAndRefreshSignalByKey(fetchSummaryPulseLifecycleCompared.typePrefix);
    const projectId = selectReportOrCurrentProjectId(getState());
    const query = selectLifecycleFiltersQuery(getState());

    return summaryApi.summaryPulseLifecycleCompared({ projectId, query }, { signal });
  },
  { serializeError }
);

export const fetchPulsesSummary = createAsyncThunk<Summary.Pulses | void>(
  `${STORE_NAME}/fetchPulsesSummary`,
  (_, { getState }) => {
    const signal = abortAndRefreshSignalByKey(fetchPulsesSummary.typePrefix);
    const isReport = selectHasMatch(ReportRoute.REPORT_PATTERN)(getState());
    const projectId = isReport
      ? selectReportProjectId(getState())
      : selectCurrentProjectId(getState());

    const query = selectFiltersQuery(getState());

    return projectScoreApi.pulsesSummary({ projectId, query }, { signal });
  },
  { serializeError }
);
